import React, {useEffect, useState, useMemo} from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListSubheader,
    makeStyles,
    Checkbox,
    ListItemText,
    Radio,
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import {observer} from 'mobx-react-lite';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import RootStore from '../../../Core/mobx/RootStore';
import {
    productCodesToLegacyKeys,
    legacyKeysToProductCodes,
    legacyKeyToCarrierName,
    PRODUCT_CODE_TO_LEGACY_KEY,
} from '../../../Core/utils/productCodeMapping';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';

const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        width: '100%',
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        paddingLeft: theme.spacing(3),
    },
    primaryProduct: {
        fontWeight: 'bold',
        backgroundColor: '#e3f2fd',
    },
    fallbackProduct: {
        backgroundColor: '#f5f5f5',
    },
    starIcon: {
        color: 'gold',
        fontSize: '1rem',
        marginLeft: theme.spacing(1),
    },
    groupHeader: {
        fontWeight: 'bold',
        backgroundColor: theme.palette.grey[200],
        fontSize: '0.875rem',
        textTransform: 'uppercase',
    },
    checkbox: {
        padding: 0,
        marginRight: theme.spacing(1),
    },
}));

interface ProductSelectorProps {
    param: Partial<Parameter> & {
        label: string;
        name: string;
        value?: any;
        setValue?: (value: any) => void;
        options?: string[];
    };
    required?: JSX.Element | string;
    valueChanged: (p?: any, value?: any) => void;
    multiple?: boolean;
    useApprovalProducts?: boolean; // Flag to use agentProducts list for multi-select
}

const ProductSelector = ({
    param,
    required,
    valueChanged,
    multiple = false,
    useApprovalProducts = false,
}: ProductSelectorProps) => {
    const classes = useStyles();
    const parameterStore = RootStore.agentPortalStore?.parameterStore;
    const prequal = parameterStore?.prequal;
    const qualifiedProducts = parameterStore?.qualifiedProducts;
    const recommendedProductsResponse = parameterStore?.recommendedProductsResponse;

    // Get the recommended products from prequal
    const primaryProductType = useMemo(() => {
        return prequal?.recommendedProductPrimary?.value || '';
    }, [prequal?.recommendedProductPrimary?.value]);

    const fallbackProductType = useMemo(() => {
        return prequal?.recommendedProductFallback?.value || '';
    }, [prequal?.recommendedProductFallback?.value]);

    // Get selected product from prequal
    const prequalSelectedProduct = useMemo(() => {
        return prequal?.selectedProduct?.value || '';
    }, [prequal?.selectedProduct?.value]);

    // Get all products from the response
    const allProducts = useMemo(() => {
        const products =
            recommendedProductsResponse?.allProducts ||
            recommendedProductsResponse?.products ||
            qualifiedProducts ||
            [];
        return products;
    }, [recommendedProductsResponse?.allProducts, recommendedProductsResponse?.products, qualifiedProducts]);

    // If parameterStore is not ready, return a simple select with fallback options
    if (!parameterStore) {
        return (
            <FormControl className={classes.formControl}>
                <InputLabel id={`${param.name}-label`}>{required || param.label}</InputLabel>
                <Select
                    labelId={`${param.name}-label`}
                    id={param.name}
                    value={param.value || (multiple ? [] : '')}
                    onChange={(e) => valueChanged(param, e.target.value)}
                    multiple={multiple}
                    variant="outlined"
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                </Select>
            </FormControl>
        );
    }

    const eligibleProductCodes = useMemo(() => {
        return recommendedProductsResponse?.allEligibleProductCodes || [];
    }, [recommendedProductsResponse]);

    // Sort and filter products
    const sortedProducts = useMemo(() => {
        // Products to exclude from display
        const excludedProducts = ['LSWTERM'];

        // If eligibleProductCodes exists and has items, filter by it
        // Otherwise show all products
        let filtered;
        if (eligibleProductCodes.length > 0) {
            filtered = allProducts
                .filter((p: any) => eligibleProductCodes.includes(p.code))
                .filter((p: any) => !excludedProducts.includes(p.code));
        } else {
            // Show all products if no eligible codes specified
            filtered = allProducts.filter((p: any) => !excludedProducts.includes(p.code));
        }

        const sorted = filtered.slice().sort((a: any, b: any) => {
            const rankA = a.productRank || 0;
            const rankB = b.productRank || 0;
            return rankB - rankA;
        });

        return sorted;
    }, [allProducts, eligibleProductCodes]);

    // Group products by type
    const groupedProducts = useMemo(() => {
        const groups: {[key: string]: any[]} = {};

        sortedProducts.forEach((product: any) => {
            const type = product.productTypeCode;
            if (!groups[type]) {
                groups[type] = [];
            }
            groups[type].push(product);
        });

        return groups;
    }, [sortedProducts]);

    // Find primary and fallback products
    const primaryRecommendedProduct = useMemo(() => {
        // console.log('Finding primaryRecommendedProduct:', {
        //     primaryProductType,
        //     sortedProductsCount: sortedProducts.length,
        //     availableTypes: [...new Set(sortedProducts.map((p: any) => p.productTypeCode))],
        // });

        // Find the highest ranked product of the primary type
        if (primaryProductType === 'IUL') {
            const iulProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'IUL');
            // console.log(
            //     'IUL products found:',
            //     iulProducts.map((p: any) => `${p.code} (${p.name})`),
            // );
            return iulProducts[0]?.name || '';
        } else if (primaryProductType === 'Term') {
            const termProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'TERM');
            // console.log(
            //     'Term products found:',
            //     termProducts.map((p: any) => `${p.code} (${p.name})`),
            // );
            return termProducts[0]?.name || '';
        } else if (primaryProductType === 'Whole Life') {
            const fexpProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'FEXP');
            // console.log(
            //     'Whole Life products found:',
            //     fexpProducts.map((p: any) => `${p.code} (${p.name})`),
            // );
            return fexpProducts[0]?.name || '';
        } else if (primaryProductType === 'VUL') {
            const vulProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'VUL');
            return vulProducts[0]?.name || '';
        } else if (primaryProductType === 'Annuity') {
            const annuityProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'ANNUITY');
            return annuityProducts[0]?.name || '';
        } else if (primaryProductType === 'ADB') {
            const adbProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'ADB');
            return adbProducts[0]?.name || '';
        }
        // If no primary type match, return the highest ranked product overall
        const defaultProduct = sortedProducts[0]?.name || '';
        // console.log('Using default product:', defaultProduct);
        return defaultProduct;
    }, [primaryProductType, sortedProducts]);

    const fallbackRecommendedProduct = useMemo(() => {
        // Find the highest ranked product of the fallback type
        if (fallbackProductType === 'Term') {
            const termProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'TERM');
            return termProducts[0]?.name || '';
        } else if (fallbackProductType === 'IUL') {
            const iulProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'IUL');
            return iulProducts[0]?.name || '';
        } else if (fallbackProductType === 'Whole Life') {
            const fexpProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'FEXP');
            return fexpProducts[0]?.name || '';
        } else if (fallbackProductType === 'VUL') {
            const vulProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'VUL');
            return vulProducts[0]?.name || '';
        } else if (fallbackProductType === 'Annuity') {
            const annuityProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'ANNUITY');
            return annuityProducts[0]?.name || '';
        } else if (fallbackProductType === 'ADB') {
            const adbProducts = sortedProducts.filter((p: any) => p.productTypeCode === 'ADB');
            return adbProducts[0]?.name || '';
        }
        return '';
    }, [fallbackProductType, sortedProducts]);

    // Get the selected product code from approval (which was copied from prequal)
    const approvalSelectedProductCode = useMemo(() => {
        const approval = RootStore.agentPortalStore?.parameterStore?.approval;
        const selectedCode = approval?.selectedProductCode?.value || '';
        return selectedCode;
    }, []);

    // Handle conversion of old product name values to codes and set defaults
    useEffect(() => {
        // Only proceed if products are loaded
        if (sortedProducts.length === 0) {
            return;
        }

        // For carrier quoted fields, check if we have a product code stored instead of a name
        if (
            (param.name === 'firstCarrierQuoted' ||
                param.name === 'secondCarrierQuoted' ||
                param.name === 'reapplyCarrierQuoted') &&
            param.value
        ) {
            const isCode = sortedProducts.some((p: any) => p.code === param.value);
            const isLegacyKey = Object.keys(PRODUCT_CODE_TO_LEGACY_KEY).some(
                (code) => PRODUCT_CODE_TO_LEGACY_KEY[code] === param.value,
            );

            if (isCode || isLegacyKey) {
                // Need to convert code/legacy key to product name
                const product = sortedProducts.find((p: any) => p.code === param.value);
                if (product) {
                    const legacyKey = productCodesToLegacyKeys([param.value])[0] || param.value;
                    const mappedCarrierName = legacyKeyToCarrierName(legacyKey);
                    const finalCarrierName =
                        mappedCarrierName !== legacyKey ? mappedCarrierName : product.name || param.value;
                    // console.log(
                    //     `Converting ${param.name} from code "${param.value}" to carrier name "${finalCarrierName}"`,
                    // );
                    valueChanged(param, finalCarrierName);

                    // Also ensure the code field is set
                    const approval = RootStore.agentPortalStore?.parameterStore?.approval;
                    if (param.name === 'firstCarrierQuoted' && approval?.firstCarrierQuotedCode) {
                        approval.firstCarrierQuotedCode.setValue(param.value);
                    } else if (param.name === 'secondCarrierQuoted' && approval?.secondCarrierQuotedCode) {
                        approval.secondCarrierQuotedCode.setValue(param.value);
                    } else if (param.name === 'reapplyCarrierQuoted' && approval?.reapplyCarrierQuotedCode) {
                        approval.reapplyCarrierQuotedCode.setValue(param.value);
                    }
                    return;
                }
            }
        }

        // Check if the current value is a product name instead of a code (for other fields)
        if (
            param.value &&
            param.name !== 'firstCarrierQuoted' &&
            param.name !== 'secondCarrierQuoted' &&
            param.name !== 'reapplyCarrierQuoted'
        ) {
            const isName = sortedProducts.some((p: any) => p.name === param.value);
            const isCode = sortedProducts.some((p: any) => p.code === param.value);

            if (isName && !isCode) {
                // Convert name to code
                const product = sortedProducts.find((p: any) => p.name === param.value);
                if (product) {
                    valueChanged(param, product.code);
                    return;
                }
            }
        }

        // Don't override firstCarrierQuoted if it already has a value
        // The value should be set from BestFitProduct and not overridden here

        // Set default to recommended product code for preapproval fields
        const approval = RootStore.agentPortalStore?.parameterStore?.approval;

        if (!param.value && param.name === 'firstCarrierQuoted') {
            // On preapproval page, use recommendedProductCode from approval store
            const recommendedCode = approval?.recommendedProductCode?.value;

            if (recommendedCode && !approval?.firstCarrierQuotedCode?.value) {
                // console.log('Setting default firstCarrierQuoted from recommendedProductCode:', {
                //     paramName: param.name,
                //     recommendedCode,
                //     sortedProductsCount: sortedProducts.length,
                // });

                // Find the product by code
                const recommendedProduct = sortedProducts.find((p: any) => p.code === recommendedCode);
                if (recommendedProduct) {
                    // For firstCarrierQuoted, try mapping first, then fall back to carrier name
                    const legacyKey = productCodesToLegacyKeys([recommendedCode])[0] || recommendedCode;
                    const mappedCarrierName = legacyKeyToCarrierName(legacyKey);
                    const finalCarrierName =
                        mappedCarrierName !== legacyKey
                            ? mappedCarrierName
                            : recommendedProduct.name || recommendedCode;
                    valueChanged(param, finalCarrierName);

                    if (approval?.firstCarrierQuotedCode) {
                        approval.firstCarrierQuotedCode.setValue(recommendedCode);
                        // console.log('Set firstCarrierQuoted to:', finalCarrierName, 'and code:', recommendedCode);
                    }
                }
            }
        }

        // Set default for agentProducts multi-select field
        if (param.name === 'agentProducts' && (!param.value || param.value.length === 0)) {
            const recommendedCode = approval?.recommendedProductCode?.value;

            if (
                recommendedCode &&
                (!approval?.agentProductCodes?.value || approval.agentProductCodes.value.length === 0)
            ) {
                // console.log('Setting default agentProducts from recommendedProductCode:', {
                //     paramName: param.name,
                //     recommendedCode,
                // });

                // Set both legacy and product code fields
                const legacyKey = productCodesToLegacyKeys([recommendedCode])[0] || recommendedCode;
                valueChanged(param, [legacyKey]);

                if (approval?.agentProductCodes) {
                    approval.agentProductCodes.setValue([recommendedCode]);
                    // console.log('Also set agentProductCodes:', [recommendedCode]);
                }
            }
        }
    }, [param.value, approvalSelectedProductCode, sortedProducts, param.name, primaryRecommendedProduct]);

    const getTypeLabel = (type: string) => {
        switch (type) {
            case 'IUL':
                return 'IUL';
            case 'VUL':
                return 'VUL';
            case 'TERM':
                return 'Term';
            case 'FEXP':
                return 'Final Expense';
            case 'ANNUITY':
                return 'Annuity';
            case 'ADB':
                return 'ADB';
            case 'Annuity':
                return 'Annuity';
            default:
                return type;
        }
    };

    const renderProductItem = (
        product: any,
        isPrimary: boolean = false,
        isFallback: boolean = false,
        keyPrefix: string = '',
    ) => {
        const isRecommended = product.name === primaryRecommendedProduct;
        let className = classes.menuItem;
        if (isPrimary) className = `${className} ${classes.primaryProduct}`;
        else if (isFallback) className = `${className} ${classes.fallbackProduct}`;

        return (
            <MenuItem key={`${keyPrefix}${product.code}`} value={product.code} className={className}>
                {multiple ? (
                    <Checkbox
                        checked={
                            Array.isArray(param.value)
                                ? param.value.includes(product.code)
                                : param.value === product.code
                        }
                        className={classes.checkbox}
                    />
                ) : (
                    <Radio checked={selectValue === product.code} className={classes.checkbox} />
                )}
                <ListItemText primary={product.name} />
                {isRecommended && <StarIcon className={classes.starIcon} />}
            </MenuItem>
        );
    };

    // Compute the select value - handle both product codes and legacy keys
    const selectValue = (() => {
        // If products aren't loaded yet, always return empty value
        if (sortedProducts.length === 0) {
            return multiple ? [] : '';
        }

        const approval = RootStore.agentPortalStore?.parameterStore?.approval;

        if (multiple) {
            // For agentProducts field, we need to convert legacy keys to product codes for display
            if (param.name === 'agentProducts') {
                // Use agentProductCodes if available, otherwise try to convert legacy keys
                if (approval?.agentProductCodes?.value) {
                    return approval.agentProductCodes.value;
                } else if (param.value) {
                    // Convert legacy keys to product codes
                    const values = Array.isArray(param.value) ? param.value : [param.value];
                    const productCodes = legacyKeysToProductCodes(values);
                    return productCodes.filter((code: any) => sortedProducts.some((p: any) => p.code === code));
                }
                return [];
            } else {
                // For other multi-select fields
                const values = Array.isArray(param.value) ? param.value : param.value ? [param.value] : [];
                const validValues = values.filter((v: any) => sortedProducts.some((p: any) => p.code === v));
                return validValues;
            }
        } else {
            // For single select - use the corresponding product code field
            let productCodeValue = '';

            if (param.name === 'firstCarrierQuoted' && approval?.firstCarrierQuotedCode?.value) {
                productCodeValue = approval.firstCarrierQuotedCode.value;
            } else if (param.name === 'secondCarrierQuoted' && approval?.secondCarrierQuotedCode?.value) {
                productCodeValue = approval.secondCarrierQuotedCode.value;
            } else if (param.name === 'reapplyCarrierQuoted' && approval?.reapplyCarrierQuotedCode?.value) {
                productCodeValue = approval.reapplyCarrierQuotedCode.value;
            } else if (param.value) {
                // Fallback: convert legacy key to product code if needed
                productCodeValue = legacyKeysToProductCodes([param.value])[0] || param.value;
            }

            // Check if value exists in available products
            const productExists = sortedProducts.some((p: any) => p.code === productCodeValue);

            if (param.name === 'firstCarrierQuoted') {
                // console.log('ProductSelector firstCarrierQuoted selectValue:', {
                //     paramValue: param.value,
                //     productCodeValue,
                //     productExists,
                //     sortedProductCodes: sortedProducts.map((p: any) => p.code),
                //     willReturn: productCodeValue && !productExists ? '' : productCodeValue || '',
                // });
            }

            if (productCodeValue && !productExists) {
                return '';
            }
            return productCodeValue || '';
        }
    })();

    // Don't show disabled state - let the component render even if products are loading
    // The dropdown will show "Loading products..." or "None" as appropriate

    return (
        <FormControl className={classes.formControl}>
            <InputLabel id={`${param.name}-label`}>
                {param.label} {required}
            </InputLabel>
            <Select
                labelId={`${param.name}-label`}
                id={param.name}
                variant="outlined"
                value={selectValue}
                onChange={(e) => {
                    const newValue = e.target.value;
                    const approval = RootStore.agentPortalStore?.parameterStore?.approval;

                    // Update the legacy field (agentProducts)
                    if (param.name === 'agentProducts') {
                        // Convert product codes to legacy keys
                        const codes = Array.isArray(newValue) ? newValue : [newValue];
                        const legacyKeys = productCodesToLegacyKeys(codes.filter(Boolean));
                        valueChanged(param, legacyKeys);

                        // Also update agentProductCodes field if it exists
                        if (approval?.agentProductCodes) {
                            approval.agentProductCodes.setValue(codes.filter(Boolean));
                            // console.log('ProductSelector: Updated agentProductCodes:', codes);
                        }
                    } else if (param.name === 'firstCarrierQuoted') {
                        // For firstCarrierQuoted, try mapping first, then fall back to carrier name from database
                        const productCode = newValue as string;
                        const product = sortedProducts.find((p: any) => p.code === productCode);
                        const legacyKey = productCodesToLegacyKeys([productCode])[0] || productCode;
                        const mappedCarrierName = legacyKeyToCarrierName(legacyKey);
                        // Use mapped carrier name if it exists, otherwise use carrier name from database
                        const finalCarrierName =
                            mappedCarrierName !== legacyKey ? mappedCarrierName : product?.name || productCode;
                        valueChanged(param, finalCarrierName);

                        if (approval?.firstCarrierQuotedCode) {
                            approval.firstCarrierQuotedCode.setValue(productCode);
                            // console.log(
                            //     'ProductSelector: Updated firstCarrierQuoted to:',
                            //     finalCarrierName,
                            //     'and code:',
                            //     productCode,
                            // );
                        }
                    } else if (param.name === 'secondCarrierQuoted') {
                        // For secondCarrierQuoted, try mapping first, then fall back to carrier name from database
                        const productCode = newValue as string;
                        const product = sortedProducts.find((p: any) => p.code === productCode);
                        const legacyKey = productCodesToLegacyKeys([productCode])[0] || productCode;
                        const mappedCarrierName = legacyKeyToCarrierName(legacyKey);
                        // Use mapped carrier name if it exists, otherwise use carrier name from database
                        const finalCarrierName =
                            mappedCarrierName !== legacyKey ? mappedCarrierName : product?.name || productCode;
                        valueChanged(param, finalCarrierName);

                        if (approval?.secondCarrierQuotedCode) {
                            approval.secondCarrierQuotedCode.setValue(productCode);
                            // console.log(
                            //     'ProductSelector: Updated secondCarrierQuoted to:',
                            //     finalCarrierName,
                            //     'and code:',
                            //     productCode,
                            // );
                        }
                    } else if (param.name === 'reapplyCarrierQuoted') {
                        // For reapplyCarrierQuoted, try mapping first, then fall back to carrier name from database
                        const productCode = newValue as string;
                        const product = sortedProducts.find((p: any) => p.code === productCode);
                        const legacyKey = productCodesToLegacyKeys([productCode])[0] || productCode;
                        const mappedCarrierName = legacyKeyToCarrierName(legacyKey);
                        // Use mapped carrier name if it exists, otherwise use carrier name from database
                        const finalCarrierName =
                            mappedCarrierName !== legacyKey ? mappedCarrierName : product?.name || productCode;
                        valueChanged(param, finalCarrierName);

                        if (approval?.reapplyCarrierQuotedCode) {
                            approval.reapplyCarrierQuotedCode.setValue(productCode);
                            // console.log(
                            //     'ProductSelector: Updated reapplyCarrierQuoted to:',
                            //     finalCarrierName,
                            //     'and code:',
                            //     productCode,
                            // );
                        }
                    } else {
                        // For other fields, just update normally
                        valueChanged(param, newValue);
                    }
                }}
                multiple={multiple}
                renderValue={
                    multiple
                        ? (selected) => {
                              // Convert codes back to names for display
                              const selectedCodes = selected as string[];
                              const names = selectedCodes.map((code) => {
                                  const product = sortedProducts.find((p: any) => p.code === code);
                                  return product?.name || code;
                              });
                              return names.join(', ');
                          }
                        : (selected) => {
                              // Convert single code back to name for display
                              if (selected && sortedProducts.length > 0) {
                                  const product = sortedProducts.find((p: any) => p.code === selected);
                                  return product?.name || selected;
                              }
                              return selected || '';
                          }
                }
            >
                {sortedProducts.length === 0 ? (
                    <MenuItem value="" disabled>
                        <em>Loading products...</em>
                    </MenuItem>
                ) : (
                    Object.entries(groupedProducts)
                        .sort(([typeA], [typeB]) => {
                            // Find primary and fallback types
                            const primaryType = sortedProducts.find(
                                (p: any) => p.name === primaryRecommendedProduct,
                            )?.productTypeCode;
                            const fallbackType = sortedProducts.find(
                                (p: any) => p.name === fallbackRecommendedProduct,
                            )?.productTypeCode;

                            if (typeA === primaryType) return -1;
                            if (typeB === primaryType) return 1;
                            if (typeA === fallbackType) return -1;
                            if (typeB === fallbackType) return 1;
                            return 0;
                        })
                        .map(([type, products]) => {
                            const isPrimaryType =
                                sortedProducts.find((p: any) => p.name === primaryRecommendedProduct)
                                    ?.productTypeCode === type;
                            const isFallbackType =
                                sortedProducts.find((p: any) => p.name === fallbackRecommendedProduct)
                                    ?.productTypeCode === type;

                            let headerLabel = getTypeLabel(type);
                            if (isPrimaryType) {
                                headerLabel = `⭐ ${headerLabel} (Recommended)`;
                            } else if (isFallbackType) {
                                headerLabel = `${headerLabel} (Alternative)`;
                            }

                            // Sort products within group by rank
                            const sortedGroupProducts = [...products].sort((a: any, b: any) => {
                                const rankA = a.productRank || 0;
                                const rankB = b.productRank || 0;
                                return rankB - rankA;
                            });

                            return [
                                <ListSubheader key={`${type}-header`} className={classes.groupHeader}>
                                    {headerLabel}
                                </ListSubheader>,
                                ...sortedGroupProducts.map((product: any) => {
                                    const isRecommended = product.name === primaryRecommendedProduct;
                                    return renderProductItem(product, isRecommended, false, `${type}-${product.code}-`);
                                }),
                            ];
                        })
                        .flat()
                )}
            </Select>
        </FormControl>
    );
};

export default observer(ProductSelector);
